import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import { VENDOR_TYPE_LABELS, PROJECT_STATUS_COLORS } from "@/lib/projectUtils";

export default function VendorDetail() {
  const { can } = useAuth();
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [activity, setActivity] = useState([]);
  const [ratings, setRatings] = useState(null);

  useEffect(() => {
    (async () => {
      const [vd, rt] = await Promise.all([
        api.get(`/vendors/${vendorId}`),
        api.get(`/vendors/${vendorId}/ratings`, { params: { limit: 10 } }),
      ]);
      setData(vd.data);
      setRatings(rt.data);
      // Phase 6 — /vendors/{id}/ledger is pnl_read gated; skip for non-Accounts/Owner.
      if (can("pnl_read")) {
        try {
          const ld = await api.get(`/vendors/${vendorId}/ledger`);
          setLedger(ld.data);
        } catch {
          setLedger(null);
        }
      } else {
        setLedger(null);
      }
      // Pull recent activity entries that reference this vendor (best-effort)
      try {
        const { data: a } = await api.get("/dashboard/recent-activity", { params: { n: 60 } });
        const items = (a.items || []).filter((x) => x.meta?.vendor_id === vendorId);
        setActivity(items);
      } catch {}
    })();
  }, [vendorId, can]);

  if (!data) return <Layout><div className="kicker">Loading</div></Layout>;
  const { vendor, stats } = data;

  return (
    <Layout>
      <div className="fade-up" data-testid="vendor-detail-page">
        <button onClick={() => navigate(-1)} className="kicker mb-3 flex items-center gap-1 text-brand-muted">
          <ArrowLeft size={12} /> BACK
        </button>

        <div className="mb-8">
          <h1 className="font-display" data-testid="vendor-name">{vendor.name}</h1>
          <div className="text-sm text-brand-muted mt-1.5 flex flex-wrap gap-3 items-center">
            <span className="px-2 py-0.5 text-[10px] tracking-[0.16em] uppercase" style={{ background: "var(--brand-paper-soft)", color: "var(--brand-ink)" }}>
              {VENDOR_TYPE_LABELS[vendor.type]}{vendor.type === "agency" ? ` · ${vendor.crew_capacity}` : ""}
            </span>
            <span>{vendor.phone_display || "—"}</span>
            {vendor.email && <span>· {vendor.email}</span>}
            {vendor.city && <span>· {vendor.city}</span>}
            {!vendor.active && <span className="px-2 py-0.5 text-[10px] tracking-[0.16em] uppercase" style={{ background: "var(--brand-danger-bg)", color: "var(--brand-danger)" }}>INACTIVE</span>}
          </div>
          {vendor.services?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid="vendor-services">
              {vendor.services.map((s) => (
                <span key={s} className="px-2 py-0.5 text-[10px] tracking-[0.16em] uppercase" style={{ background: "var(--brand-paper-soft)", color: "var(--brand-ink)" }}>{s}</span>
              ))}
            </div>
          )}
          {vendor.notes && <div className="text-sm mt-3 text-brand-muted">{vendor.notes}</div>}
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-brand-border mb-8" data-testid="vendor-stats">
          <Stat label="Avg rating"
                value={ratings?.rating_count
                  ? `${ratings.avg_rating?.toFixed(1)} ★`
                  : "—"}
                hint={ratings?.rating_count
                  ? `${ratings.rating_count} rating${ratings.rating_count === 1 ? "" : "s"}`
                  : "no ratings yet"}
                testId="stat-rating" />
          <Stat label="Projects worked" value={stats.projects_worked} testId="stat-projects" />
          <Stat label="Total paid" value={fmtINR(stats.total_paid)} testId="stat-paid" />
          <Stat label="Outstanding" value={fmtINR(stats.outstanding)} color={stats.outstanding > 0 ? "var(--brand-danger)" : undefined} testId="stat-owed" />
        </div>

        {/* Phase 5 Slice B-2 — Recent ratings panel */}
        {ratings && ratings.recent && ratings.recent.length > 0 && (
          <div className="bg-white border border-brand-border p-6 mb-10" data-testid="vendor-ratings-panel">
            <h2 className="font-display text-xl mb-4" style={{ color: "var(--brand-ink)" }}>Recent ratings</h2>
            <ul className="divide-y divide-brand-border">
              {ratings.recent.map((r) => (
                <li key={r.id} className="py-3 flex items-start gap-3" data-testid={`vendor-rating-${r.id}`}>
                  <span className="text-base" style={{ color: "var(--brand-champagne)" }}>
                    {"★".repeat(r.stars)}<span className="text-brand-muted">{"★".repeat(5 - r.stars)}</span>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm" style={{ color: "var(--brand-ink)" }}>
                      <span className="kicker mr-2">{r.service_type}</span>
                      {r.project_name}
                    </div>
                    {r.comment && (
                      <div className="text-xs text-brand-muted mt-1">&ldquo;{r.comment}&rdquo;</div>
                    )}
                  </div>
                  <div className="text-[10px] text-brand-muted whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </div>
                </li>
              ))}
            </ul>
            {ratings.rating_count > 10 && (
              <p className="text-xs text-brand-muted mt-3 italic" data-testid="vendor-ratings-more">
                Showing 10 of {ratings.rating_count}. (Full history coming in a later phase.)
              </p>
            )}
          </div>
        )}

        {/* Ledger */}
        <div className="bg-white border border-brand-border p-6 mb-10" data-testid="vendor-ledger">
          <h2 className="font-display text-xl mb-4" style={{ color: "var(--brand-ink)" }}>Ledger</h2>
          {!ledger || ledger.projects.length === 0 ? (
            <p className="text-sm text-brand-muted italic">No projects yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.18em] uppercase text-brand-muted">
                <tr><th className="text-left py-2">Project</th><th className="text-left py-2">Wedding</th><th className="text-left py-2">Status</th><th className="text-right py-2">Services</th><th className="text-right py-2">Cost</th><th className="text-right py-2">Paid</th><th className="text-right py-2">Owed</th></tr>
              </thead>
              <tbody>
                {ledger.projects.map((r) => (
                  <tr key={r.project_id} className="border-t border-brand-border cursor-pointer hover:bg-brand-paper"
                      onClick={() => navigate(`/projects/${r.project_id}`)} data-testid={`ledger-row-${r.project_id}`}>
                    <td className="py-2">{r.client_name}</td>
                    <td className="py-2 text-brand-muted">{fmtDate(r.wedding_date)}</td>
                    <td className="py-2">{r.status && <span className="px-2 py-0.5 text-[10px] tracking-[0.16em] uppercase" style={{ background: PROJECT_STATUS_COLORS[r.status], color: "#fff" }}>{r.status}</span>}</td>
                    <td className="py-2 text-right">{r.services_assigned}</td>
                    <td className="py-2 text-right">{fmtINR(r.total_cost)}</td>
                    <td className="py-2 text-right">{fmtINR(r.total_paid)}</td>
                    <td className="py-2 text-right" style={{ color: r.owed > 0 ? "var(--brand-danger)" : "var(--brand-success)" }}>{fmtINR(r.owed)}</td>
                  </tr>
                ))}
                <tr className="border-t border-brand-border font-medium">
                  <td className="py-2" colSpan="4">Totals</td>
                  <td className="py-2 text-right">{fmtINR(ledger.totals.cost)}</td>
                  <td className="py-2 text-right">{fmtINR(ledger.totals.paid)}</td>
                  <td className="py-2 text-right" style={{ color: ledger.totals.owed > 0 ? "var(--brand-danger)" : "var(--brand-success)" }}>{fmtINR(ledger.totals.owed)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Activity */}
        {activity.length > 0 && (
          <div className="bg-white border border-brand-border p-6" data-testid="vendor-activity">
            <h2 className="font-display text-xl mb-4" style={{ color: "var(--brand-ink)" }}>Activity</h2>
            <ul className="space-y-2 text-sm">
              {activity.map((a) => (
                <li key={a.id} className="flex items-center gap-3 py-1.5 border-b border-brand-border last:border-0">
                  <span className="px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase" style={{ background: "var(--brand-paper-soft)", color: "var(--brand-ink)" }}>{a.type.replace(/_/g, " ")}</span>
                  <span className="text-brand-muted">{a.actor}</span>
                  <span className="ml-auto text-brand-muted text-xs">{fmtDate(a.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value, hint, color, testId }) {
  return (
    <div className="bg-white px-5 py-4" data-testid={testId}>
      <div className="kicker mb-1.5">{label}</div>
      <div className="font-display text-2xl" style={{ color: color || "var(--brand-ink)" }}>{value}</div>
      {hint && <div className="text-[10px] tracking-[0.18em] uppercase text-brand-muted mt-1">{hint}</div>}
    </div>
  );
}
